import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "semantic-ui-react";

import { t } from "../../../constant/translations";
import { amountToCurrency, getAppCurrency } from "../../../helpers/currency";
import { formatDate } from "../../../helpers/date";
import { previewAmount } from "../../../helpers/contract-service";
import { CalendarInput } from "../../common/calendar-input";

const CalWrap = styled.div`
  & .ui.input {
    width: 100%;
  }
  & .ui.input > input {
    width: 100%;
    border: 1px solid #e7e5e4 !important;
    border-radius: 9px !important;
    padding: 10px 12px !important;
    font-size: 15px !important;
  }
  & .ui.input > input:focus {
    border-color: #2185d0 !important;
    box-shadow: 0 0 0 3px rgba(33, 133, 208, 0.14) !important;
  }
`;

const Form = styled.div`
  border: 1px solid #2185d0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
  background: #fbfdff;
  box-shadow: 0 8px 24px rgba(33, 133, 208, 0.1);
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 15px;
  margin-bottom: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #78716c;
  margin: 12px 0 7px;
  font-weight: 700;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 10px;
  & > div {
    flex: 1;
  }
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  & .suffix {
    position: absolute;
    right: 12px;
    color: #78716c;
    font-weight: 700;
    pointer-events: none;
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #e7e5e4;
  border-radius: 9px;
  padding: 10px 12px;
  font-size: 15px;
  font-family: inherit;
  color: #1c1917;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #2185d0;
    box-shadow: 0 0 0 3px rgba(33, 133, 208, 0.14);
  }
`;

const Segment = styled.div`
  display: flex;
  border: 1px solid #e7e5e4;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
`;

const SegOpt = styled.div`
  flex: 1;
  text-align: center;
  padding: 9px 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${(props) => (props.on ? "#fff" : "#5b6068")};
  background: ${(props) => (props.on ? "#1c1917" : "transparent")};
  cursor: pointer;
  border-right: 1px solid #e7e5e4;
  &:last-child {
    border-right: none;
  }
`;

const Preview = styled.div`
  margin-top: 14px;
  background: ${(props) => (props.ending ? "#fdecea" : "#e8f8ee")};
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewL = styled.div`
  font-size: 13px;
  color: ${(props) => (props.ending ? "#b23a29" : "#1f9a52")};
  font-weight: 700;
`;

const PreviewV = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${(props) => (props.ending ? "#b23a29" : "#178347")};
  & .old {
    font-size: 14px;
    color: #7bbd97;
    text-decoration: line-through;
    margin-right: 8px;
  }
`;

const Hint = styled.div`
  font-size: 12.5px;
  color: #78716c;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

export const ChangeForm = ({ service, initial, editing, onSave, onCancel }) => {
  const [type, setType] = useState(initial ? initial.type : "pct");
  const [value, setValue] = useState(
    initial
      ? initial.type === "pct"
        ? String(initial.value)
        : initial.new_amount != null
        ? String(initial.new_amount)
        : ""
      : "10"
  );
  const [eff, setEff] = useState(
    initial ? initial.effective_from : new Date().toISOString().slice(0, 10)
  );
  const [note, setNote] = useState(initial ? initial.note || "" : "");

  const base = parseFloat(service.amount);
  const newAmount = previewAmount(base, type, value);

  function save() {
    const payload = { type, effective_from: eff, note };
    if (type === "pct") {
      payload.value = String(parseFloat(String(value).replace(",", ".")) || 0);
      payload.new_amount = String(newAmount);
    } else if (type === "price") {
      payload.new_amount = String(newAmount);
    }
    onSave(payload);
  }

  return (
    <Form>
      <Title>
        {editing
          ? t("contract.line.edit_scheduled")
          : t("contract.line.plan_change")}
      </Title>

      <FieldLabel>{t("contract.line.change_type_price")}</FieldLabel>
      <Segment>
        <SegOpt on={type === "price"} onClick={() => setType("price")}>
          {t("contract.line.change_type_price")}
        </SegOpt>
        <SegOpt on={type === "pct"} onClick={() => setType("pct")}>
          {t("contract.line.change_type_pct")}
        </SegOpt>
        <SegOpt on={type === "end"} onClick={() => setType("end")}>
          {t("contract.line.change_type_end")}
        </SegOpt>
      </Segment>

      {type !== "end" && (
        <FieldRow style={{ marginTop: 4 }}>
          <div>
            <FieldLabel>
              {type === "pct"
                ? t("contract.line.change_type_pct")
                : t("contract.line.amount_per_month")}
            </FieldLabel>
            <InputWrap>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputMode="decimal"
              />
              <span className="suffix">{type === "pct" ? "%" : "€"}</span>
            </InputWrap>
          </div>
          <div>
            <FieldLabel>{t("contract.line.effective_from")}</FieldLabel>
            <CalWrap>
              <CalendarInput
                name="eff"
                value={eff}
                onChange={(e) => setEff(e.target.value)}
              />
            </CalWrap>
          </div>
        </FieldRow>
      )}
      {type === "end" && (
        <div style={{ marginTop: 4 }}>
          <FieldLabel>{t("contract.line.change_type_end")}</FieldLabel>
          <CalWrap>
            <CalendarInput
              name="eff"
              value={eff}
              onChange={(e) => setEff(e.target.value)}
            />
          </CalWrap>
        </div>
      )}

      <FieldLabel>{t("contract.line.change_reason")}</FieldLabel>
      <Input value={note} onChange={(e) => setNote(e.target.value)} />

      {type !== "end" ? (
        <Preview>
          <PreviewL>
            {t("contract.line.preview_new_from")} {formatDate(eff)}
          </PreviewL>
          <PreviewV>
            <span className="old">
              {amountToCurrency(base, getAppCurrency(), getAppCurrency())}
            </span>
            {amountToCurrency(newAmount, getAppCurrency(), getAppCurrency())}
          </PreviewV>
        </Preview>
      ) : (
        <Preview ending>
          <PreviewL ending>
            {t("contract.line.preview_terminate_from")}
          </PreviewL>
          <PreviewV ending>
            {t("contract.line.preview_from_short")} {formatDate(eff)}
          </PreviewV>
        </Preview>
      )}

      <Hint>{t("contract.line.scheduled_one_per_row_note")}</Hint>

      <Actions>
        <Button basic size="small" onClick={onCancel}>
          {t("contract.line.cancel")}
        </Button>
        <Button primary size="small" onClick={save}>
          {editing
            ? t("contract.line.save_change")
            : type === "end"
            ? t("contract.line.plan_end_btn")
            : t("contract.line.plan_change_btn")}
        </Button>
      </Actions>
    </Form>
  );
};
